import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { ConfirmationModal } from '../../components/ui/ConfirmationModal';
import { Pagination } from '../../components/ui/Pagination';
import {
  listTgChannels,
  createTgChannel,
  updateTgChannel,
  deleteTgChannel,
  type TgChannel,
  type TgChannelStatus,
  type TgChannelGrade,
  type TgChannelFormData,
} from '../../services/tgChannelService';

const CATEGORY_LABELS: Record<string, string> = {
  traffic: '🔥 Реклама и трафик',
  bloggers: '✍️ Блогеры / арбитражники',
  teams: '👥 Арбитражные команды',
  conferences: '🎪 Конференции',
  partners: '🤝 Партнёрки и сервисы',
  jobs: '💼 Вакансии',
  creatives: '🎨 Креативы',
  gossip: '💬 Сплетни',
  seo: '🔍 SEO',
  misc: '📦 Разное',
};

const STATUS_CONFIG: Record<TgChannelStatus, { label: string; variant: 'success' | 'warning' | 'danger' | 'default' }> = {
  prospect:  { label: 'Prospect',   variant: 'default' },
  contacted: { label: 'Contacted',  variant: 'warning' },
  agreed:    { label: 'Agreed',     variant: 'success' },
  posted:    { label: 'Posted',     variant: 'success' },
  skip:      { label: 'Skip',       variant: 'danger' },
};

function pctBadgeClass(pct: number): string {
  if (pct >= 20) return 'text-emerald-700 bg-emerald-50 border border-emerald-200';
  if (pct >= 10) return 'text-blue-700 bg-blue-50 border border-blue-200';
  if (pct >= 5)  return 'text-gray-600 bg-gray-100 border border-gray-200';
  return 'text-orange-700 bg-orange-50 border border-orange-200';
}

const GRADE_CONFIG: Record<TgChannelGrade, { label: string; cls: string }> = {
  'A+': { label: 'A+', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold' },
  'A':  { label: 'A',  cls: 'bg-emerald-50  text-emerald-700 border border-emerald-200 font-semibold' },
  'B':  { label: 'B',  cls: 'bg-blue-50     text-blue-700    border border-blue-200    font-semibold' },
  'C':  { label: 'C',  cls: 'bg-yellow-50   text-yellow-700  border border-yellow-200  font-semibold' },
  'D':  { label: 'D',  cls: 'bg-orange-50   text-orange-700  border border-orange-200  font-semibold' },
  'F':  { label: 'F',  cls: 'bg-red-50      text-red-700     border border-red-200     font-semibold' },
};

function GradeBadge({ grade }: { grade: TgChannelGrade | null }) {
  if (!grade) return <span className="text-gray-300 text-xs">—</span>;
  const cfg = GRADE_CONFIG[grade] ?? GRADE_CONFIG['F'];
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
}

function fmtNum(n: number): string {
  return n >= 1000 ? (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : String(n);
}

const EMPTY_FORM: TgChannelFormData = {
  category: 'traffic',
  name: '',
  handle: '',
  subscribers: 0,
  reach: 0,
  price: '',
  status: 'prospect',
  comment: '',
  contact: '',
};

type SortKey = 'name' | 'handle' | 'subscribers' | 'reach' | 'pct' | 'status';
type SortDir = 'asc' | 'desc';

function SortableTh({
  label, colKey, sortKey, sortDir, onSort, className = '',
}: {
  label: string; colKey: SortKey; sortKey: SortKey | null; sortDir: SortDir; onSort: (k: SortKey) => void; className?: string;
}) {
  const active = sortKey === colKey;
  return (
    <th
      onClick={() => onSort(colKey)}
      className={`px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-800 whitespace-nowrap ${className}`}
    >
      {label}
      <span className="ml-1 inline-block w-3 text-center">
        {active ? (sortDir === 'asc' ? '↑' : '↓') : <span className="text-gray-300">↕</span>}
      </span>
    </th>
  );
}

export const TgChannelManagementPage: React.FC = () => {
  const [channels, setChannels] = useState<TgChannel[]>([]);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gradeFilter, setGradeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState<TgChannel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; channel: TgChannel | null }>({
    open: false,
    channel: null,
  });
  const [savingId, setSavingId] = useState<number | null>(null);

  // Debounce timer for inline edits
  const debounceTimers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTgChannels({
        page: currentPage,
        per_page: 100,
        search: search || undefined,
        category: categoryFilter || undefined,
        status: statusFilter || undefined,
        ai_grade: gradeFilter || undefined,
      });
      setChannels(res.data);
      setTotalPages(res.last_page);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [currentPage, search, categoryFilter, statusFilter, gradeFilter]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  useEffect(() => {
    if (success || error) {
      const t = setTimeout(() => { setSuccess(null); setError(null); }, 4000);
      return () => clearTimeout(t);
    }
  }, [success, error]);

  const handleInlineChange = (id: number, field: 'price' | 'comment' | 'status' | 'contact', value: string) => {
    setChannels(prev => prev.map(ch => ch.id === id ? { ...ch, [field]: value } : ch));

    clearTimeout(debounceTimers.current[id]);
    debounceTimers.current[id] = setTimeout(async () => {
      setSavingId(id);
      try {
        await updateTgChannel(id, { [field]: value });
      } catch {
        setError('Failed to save change');
      } finally {
        setSavingId(null);
      }
    }, 600);
  };

  const handleDelete = async () => {
    if (!deleteConfirm.channel) return;
    try {
      await deleteTgChannel(deleteConfirm.channel.id);
      setSuccess('Channel deleted.');
      setDeleteConfirm({ open: false, channel: null });
      fetchChannels();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      if (sortDir === 'desc') { setSortKey(null); }
      else { setSortDir('desc'); }
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortedChannels = sortKey
    ? [...channels].sort((a, b) => {
        let va: string | number, vb: string | number;
        if (sortKey === 'name')        { va = a.name;        vb = b.name; }
        else if (sortKey === 'handle') { va = a.handle;      vb = b.handle; }
        else if (sortKey === 'subscribers') { va = a.subscribers; vb = b.subscribers; }
        else if (sortKey === 'reach')  { va = a.reach;       vb = b.reach; }
        else if (sortKey === 'pct')    { va = a.subscribers > 0 ? a.reach / a.subscribers : 0; vb = b.subscribers > 0 ? b.reach / b.subscribers : 0; }
        else                           { va = a.status;      vb = b.status; }
        const cmp = typeof va === 'string' ? va.localeCompare(vb as string) : (va as number) - (vb as number);
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : channels;

  // Stats
  const agreed = channels.filter(c => c.status === 'agreed').length;
  const posted = channels.filter(c => c.status === 'posted').length;
  const priced = channels.filter(c => c.price?.trim()).length;
  const totalSubs = channels.reduce((s, c) => s + c.subscribers, 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">TG Channels Tracker</h1>
          <p className="text-gray-500 mt-1 text-sm">Manage Telegram advertising channels, prices and statuses</p>
        </div>
        <Button onClick={() => { setEditingChannel(null); setShowModal(true); }}>
          + Add Channel
        </Button>
      </div>

      {success && <Alert variant="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      {error   && <Alert variant="error"   onClose={() => setError(null)}>{error}</Alert>}

      {/* Stats bar */}
      <div className="flex flex-wrap gap-4 text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3 border border-gray-200">
        <span>Showing <strong className="text-gray-800">{channels.length}</strong> / {total}</span>
        <span>Total reach: <strong className="text-gray-800">{(totalSubs / 1000).toFixed(0)}k subs</strong></span>
        <span>Priced: <strong className="text-gray-800">{priced}</strong></span>
        <span>Agreed: <strong className="text-emerald-700">{agreed}</strong></span>
        <span>Posted: <strong className="text-violet-700">{posted}</strong></span>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-wrap gap-3 items-center">
          <Input
            type="text"
            placeholder="Search channel or handle…"
            value={search}
            onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            className="w-56"
          />
          <select
            value={categoryFilter}
            onChange={e => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
            className="input w-48"
          >
            <option value="">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
              <option key={val} value={val}>{label}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="input w-40"
          >
            <option value="">All statuses</option>
            {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
              <option key={val} value={val}>{cfg.label}</option>
            ))}
          </select>
          <select
            value={gradeFilter}
            onChange={e => { setGradeFilter(e.target.value); setCurrentPage(1); }}
            className="input w-32"
          >
            <option value="">All grades</option>
            {(['A+', 'A', 'B', 'C', 'D', 'F'] as TgChannelGrade[]).map(g => (
              <option key={g} value={g}>Grade {g}</option>
            ))}
          </select>
          {(search || categoryFilter || statusFilter || gradeFilter) && (
            <button
              type="button"
              className="text-sm text-gray-500 hover:text-gray-700 underline"
              onClick={() => { setSearch(''); setCategoryFilter(''); setStatusFilter(''); setGradeFilter(''); setCurrentPage(1); }}
            >
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 overflow-x-auto">
        {loading ? (
          <div className="text-gray-400 text-sm py-12 text-center">Loading…</div>
        ) : channels.length === 0 ? (
          <div className="text-gray-400 text-sm py-12 text-center">No channels match your filters</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8">#</th>
                <th className="px-3 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">AI</th>
                <SortableTh label="Channel"  colKey="name"        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-left" />
                <SortableTh label="Handle"   colKey="handle"      sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-left" />
                <SortableTh label="Subs"     colKey="subscribers" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableTh label="Reach"    colKey="reach"       sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-right" />
                <SortableTh label="Охват %"  colKey="pct"         sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-right" />
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">Price</th>
                <SortableTh label="Status"   colKey="status"      sortKey={sortKey} sortDir={sortDir} onSort={handleSort} className="text-left w-32" />
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Comment</th>
                <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-32">Contact</th>
                <th className="px-3 py-3 w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {sortedChannels.map((ch, idx) => {
                const pct = ch.subscribers > 0 ? (ch.reach / ch.subscribers) * 100 : 0;
                const pctStr = pct > 0 ? pct.toFixed(1) + '%' : '—';
                const url = `https://t.me/${ch.handle.replace('@', '')}`;
                const saving = savingId === ch.id;

                return (
                  <tr key={ch.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-3 py-2 text-gray-400 text-xs text-right">{ch.fixed_order || idx + 1}</td>
                    <td className="px-3 py-2 text-center">
                      <div title={ch.ai_recommendation ?? undefined} className="cursor-help inline-block">
                        <GradeBadge grade={ch.ai_grade} />
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-gray-800 leading-snug max-w-[220px]">{ch.name}</div>
                      <div className="text-xs text-gray-400">{CATEGORY_LABELS[ch.category] ?? ch.category}</div>
                      {ch.ai_recommendation && (
                        <div className="text-xs text-gray-500 mt-1 max-w-[220px] leading-snug italic">{ch.ai_recommendation}</div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-indigo-600 hover:underline text-xs whitespace-nowrap"
                      >
                        {ch.handle}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-gray-800 whitespace-nowrap">
                      {ch.subscribers > 0 ? fmtNum(ch.subscribers) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-500 whitespace-nowrap">
                      {ch.reach > 0 ? fmtNum(ch.reach) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {pct > 0 ? (
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${pctBadgeClass(pct)}`}>
                          {pctStr}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        className={`input text-xs py-1 w-full ${saving ? 'opacity-50' : ''}`}
                        placeholder="e.g. €150"
                        value={ch.price ?? ''}
                        onChange={e => handleInlineChange(ch.id, 'price', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <select
                        className={`input text-xs py-1 w-full ${saving ? 'opacity-50' : ''}`}
                        value={ch.status}
                        onChange={e => handleInlineChange(ch.id, 'status', e.target.value)}
                      >
                        {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                          <option key={val} value={val}>{cfg.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2">
                      <textarea
                        className={`input text-xs py-1 w-full min-w-[150px] resize-none ${saving ? 'opacity-50' : ''}`}
                        rows={1}
                        placeholder="Add notes…"
                        value={ch.comment ?? ''}
                        onChange={e => handleInlineChange(ch.id, 'comment', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="text"
                        className={`input text-xs py-1 w-full ${saving ? 'opacity-50' : ''}`}
                        placeholder="@username"
                        value={ch.contact ?? ''}
                        onChange={e => handleInlineChange(ch.id, 'contact', e.target.value)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex gap-1">
                        <button
                          type="button"
                          onClick={() => { setEditingChannel(ch); setShowModal(true); }}
                          className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm({ open: true, channel: ch })}
                          className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50"
                        >
                          Del
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {totalPages > 1 && (
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
      )}

      {showModal && (
        <TgChannelFormModal
          channel={editingChannel}
          onClose={() => { setShowModal(false); setEditingChannel(null); }}
          onSuccess={msg => { setSuccess(msg); setShowModal(false); setEditingChannel(null); fetchChannels(); }}
          onError={setError}
        />
      )}

      <ConfirmationModal
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, channel: null })}
        onConfirm={handleDelete}
        title="Delete Channel"
        message={`Delete "${deleteConfirm.channel?.name}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

// ── Form Modal ────────────────────────────────────────────────────────────────

interface FormModalProps {
  channel: TgChannel | null;
  onClose: () => void;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}

function TgChannelFormModal({ channel, onClose, onSuccess, onError }: FormModalProps) {
  const [form, setForm] = useState<TgChannelFormData>({
    category:    channel?.category    ?? 'traffic',
    name:        channel?.name        ?? '',
    handle:      channel?.handle      ?? '',
    subscribers: channel?.subscribers ?? 0,
    reach:       channel?.reach       ?? 0,
    price:       channel?.price       ?? '',
    status:      channel?.status      ?? 'prospect',
    comment:     channel?.comment     ?? '',
    contact:     channel?.contact     ?? '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (channel) {
        await updateTgChannel(channel.id, form);
        onSuccess('Channel updated.');
      } else {
        await createTgChannel(form);
        onSuccess('Channel created.');
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  const f = (field: keyof TgChannelFormData, value: string | number) =>
    setForm(prev => ({ ...prev, [field]: value }));

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={channel ? 'Edit Channel' : 'Add Channel'}
      size="large"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Category <span className="text-red-500">*</span></label>
            <select className="input w-full" value={form.category} onChange={e => f('category', e.target.value)} required>
              {Object.entries(CATEGORY_LABELS).map(([val, label]) => (
                <option key={val} value={val}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input w-full" value={form.status} onChange={e => f('status', e.target.value as TgChannelStatus)}>
              {Object.entries(STATUS_CONFIG).map(([val, cfg]) => (
                <option key={val} value={val}>{cfg.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Channel Name <span className="text-red-500">*</span></label>
          <Input type="text" value={form.name} onChange={e => f('name', e.target.value)} required placeholder="Channel display name" />
        </div>

        <div>
          <label className="label">Handle <span className="text-red-500">*</span></label>
          <Input type="text" value={form.handle} onChange={e => f('handle', e.target.value)} required placeholder="@channelhandle" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Subscribers</label>
            <Input type="number" min={0} value={form.subscribers ?? 0} onChange={e => f('subscribers', Number(e.target.value))} />
          </div>
          <div>
            <label className="label">Reach (avg views)</label>
            <Input type="number" min={0} value={form.reach ?? 0} onChange={e => f('reach', Number(e.target.value))} />
          </div>
        </div>

        <div>
          <label className="label">Price</label>
          <Input type="text" value={form.price ?? ''} onChange={e => f('price', e.target.value)} placeholder="e.g. €150" />
        </div>

        <div>
          <label className="label">Comment</label>
          <textarea
            className="input w-full resize-none"
            rows={3}
            value={form.comment ?? ''}
            onChange={e => f('comment', e.target.value)}
            placeholder="Notes, contact info, etc."
          />
        </div>

        <div>
          <label className="label">Contact</label>
          <Input type="text" value={form.contact ?? ''} onChange={e => f('contact', e.target.value)} placeholder="@username" />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={submitting}>{submitting ? 'Saving…' : 'Save'}</Button>
        </div>
      </form>
    </Modal>
  );
}

export default TgChannelManagementPage;
